import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { updatePassword } from '../../services/auth';
import { supabase } from '../../lib/supabaseClient';
import { Helmet } from 'react-helmet';

const ActualizarPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Supabase recupera la sesión a través del token en hash/query
    const verifyRecoverySession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          // Escuchar evento de recuperación de contraseña
          const { data: authSub } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
              // Sesión activa lista para actualizar contraseña
            }
          });
          return () => {
            authSub?.subscription?.unsubscribe();
          };
        }
      } catch (err) {
        console.error('Error al verificar sesión de recuperación:', err);
      } finally {
        setCheckingSession(false);
      }
    };

    verifyRecoverySession();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setIsSubmitting(true);

    try {
      await updatePassword(password);
      setSuccess(true);
      setTimeout(() => {
        navigate('/ingresar');
      }, 3000);
    } catch (err) {
      console.error('Error al actualizar contraseña:', err);
      setError(err.message || 'No se pudo actualizar la contraseña. El enlace puede haber expirado.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-grid">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg text-gold"></span>
          <p className="mt-4 text-gray-300">Verificando enlace de seguridad...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20 relative bg-dark-grid">
      <Helmet>
        <title>Nueva Contraseña - MatiMarket</title>
        <meta name="description" content="Establece una nueva contraseña para tu cuenta de MatiMarket." />
      </Helmet>

      <div className="w-full max-w-md relative z-10 glass-card p-8 rounded-2xl border border-gold/30 shadow-2xl backdrop-blur-xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gold/10 border border-gold/40 mb-4 text-gold shadow-lg shadow-gold/20">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h1 className="text-2xl font-black text-white uppercase tracking-wider">
            Nueva <span className="text-gold-gradient">Contraseña</span>
          </h1>
          <p className="text-sm text-gray-300 mt-2">
            Ingresa tu nueva contraseña para proteger tu cuenta de MatiMarket.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/20 border border-red-500/40 text-red-200 text-sm flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {success ? (
          <div className="p-5 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-center">
            <svg className="w-12 h-12 mx-auto text-emerald-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-bold text-white mb-1">¡Contraseña actualizada con éxito!</h3>
            <p className="text-sm text-gray-300">Redirigiéndote al inicio de sesión en unos segundos...</p>
            <Link
              to="/ingresar"
              className="mt-4 inline-block py-2.5 px-6 rounded-xl font-semibold text-sm text-black bg-gold hover:bg-gold-light transition"
            >
              Ir a Iniciar Sesión ahora
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="new-password" className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">
                Nueva Contraseña
              </label>
              <input
                id="new-password"
                type="password"
                required
                placeholder="Mínimo 6 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition duration-200"
              />
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">
                Confirmar Nueva Contraseña
              </label>
              <input
                id="confirm-password"
                type="password"
                required
                placeholder="Repite la nueva contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition duration-200"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 px-4 rounded-xl font-bold uppercase tracking-wider text-black bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 shadow-lg shadow-gold/25 hover:shadow-gold/40 transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  <span>Guardando nueva contraseña...</span>
                </>
              ) : (
                'Guardar Nueva Contraseña'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ActualizarPassword;
